import { useAuth } from "../../context/AuthContext";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Download, Info } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { useToast } from "../../components/ui/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { formatDuration, formatTimestamp } from "../../lib/time";
import { cn } from "../../lib/utils";
import {
  CallsPerDayChart, HourlyPickupChart, OutcomeDonut,
} from "./charts";
import { DataAvailabilityBanner } from "./data-availability-banner";
import { ExportModal } from "./export-modal";
import { useAnalytics } from "./use-analytics";
import {
  downloadAnalyticsExport,
  type AnalyticsExportFormat,
  type AnalyticsExportRequest,
} from "./analytics-export";
import { getWorkflowId } from "../home/card-routes";
import { useAgents, useWorkflows } from "../home/queries";

import { DateRangePicker, presetRange, type DateRangeValue } from "../../components/ui/date-range-picker";
import { FilterPopover, type FilterState } from "../../components/ui/filter-popover";

export function AnalyticsPage() {
  const { user } = useAuth() as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const agentId = searchParams.get("agent") || null;
  const workflowId = searchParams.get("workflow") || null;
  const hasTargetFilter = Boolean(agentId || workflowId);

  const [range, setRange] = useState<DateRangeValue>(() => ({ ...presetRange("last_7"), preset: "last_7" }));
  
  const filters: FilterState = {
    agent_id: searchParams.get("agent") || undefined,
    workflow_id: searchParams.get("workflow") || undefined,
    version_name: searchParams.get("version_name") || undefined,
    status: searchParams.get("status") || undefined,
    search: searchParams.get("search") || undefined,
    search_field: (searchParams.get("search_field") as any) || undefined,
    campaign: searchParams.get("campaign") || undefined,
    voice: searchParams.get("voice") || undefined,
    max_duration: searchParams.get("max_duration") || undefined,
    duration_operator: searchParams.get("duration_operator") || undefined,
  };

  const { data, loading, agentMeta } = useAnalytics(
    range.start,
    range.end,
    filters,
  );
  const toast = useToast();
  const [exportOpen, setExportOpen] = useState(false);
  const [recentExports, setRecentExports] = useState<Array<{
    id: string;
    name: string;
    format: AnalyticsExportFormat;
    createdAt: string;
  }>>([]);

  /* Agent dropdown state */
  const agents = useAgents();
  const workflows = useWorkflows();
  const updateFilters = (newFilters: FilterState) => {
    const params = new URLSearchParams(searchParams);
    
    // Map newFilters to URL params
    const mappings: Record<keyof FilterState, string> = {
      agent_id: "agent",
      agent_name: "agent_name",
      version_name: "version_name",
      workflow_id: "workflow",
      status: "status",
      direction: "direction",
      search: "search",
      search_field: "search_field",
      campaign: "campaign",
      voice: "voice",
      max_duration: "max_duration",
      duration_operator: "duration_operator",
      classification: "classification",
      dial_workflow_id: "dial_workflow",
      dial_agent_name: "dial_agent",
      sent_channel: "sent_channel",
    };

    Object.entries(mappings).forEach(([stateKey, paramKey]) => {
      const val = newFilters[stateKey as keyof FilterState];
      if (val) {
        params.set(paramKey, val);
      } else {
        params.delete(paramKey);
      }
    });

    setSearchParams(params, { replace: true });
  };

  const displayName = agentMeta?.agent_name
    || agents.data?.find((a) => a.agent_id === agentId)?.agent_name
    || agents.data?.find((a) => a.agent_id === agentId)?.name
    || workflows.data?.find((workflow) => getWorkflowId(workflow) === workflowId)?.agent_name
    || workflows.data?.find((workflow) => getWorkflowId(workflow) === workflowId)?.name
    || workflowId
    || agentId;
  const bestPickupWindow = data.hourlyPickup.find((window) => window.best)?.window;

  // Credits are pulse-rounded minute-equivalents, so a sub-minute pulse (e.g. 30s)
  // produces .5 values — keep one decimal rather than rounding them away.
  const formatCredits = (credits: number) => {
    const value = Number(credits) || 0;
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  };

  const buildExportRequest = (format: AnalyticsExportFormat): AnalyticsExportRequest => ({
    format,
    start: range.start,
    end: range.end,
    agentId,
    workflowId,
    targetName: hasTargetFilter ? displayName : null,
    filters: {
      search: filters.search,
      search_field: filters.search_field,
      status: filters.status,
      agent_name: filters.agent_name,
      version_name: filters.version_name,
      voice: filters.voice,
      campaign: filters.campaign,
      max_duration: filters.max_duration,
      duration_operator: filters.duration_operator || (filters.max_duration ? ">=" : undefined),
    },
  });

  const exportAnalytics = async (format: AnalyticsExportFormat, remember = true) => {
    const filename = await downloadAnalyticsExport(buildExportRequest(format));
    if (remember) {
      setRecentExports((current) => [{
        id: crypto.randomUUID(),
        name: filename,
        format,
        createdAt: formatTimestamp(new Date(), user?.timezone),
      }, ...current].slice(0, 8));
    }
    toast.success("Export downloaded", filename);
  };

  return (
    <div className="space-y-4 px-4 sm:px-5 md:px-6 pb-6 pt-3 sm:pt-4">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">

            <h1 className="text-sm font-semibold text-ink">
              {hasTargetFilter ? `Analytics · ${displayName}` : "Strategic Analytics"}
            </h1>
            {hasTargetFilter && (
              <Badge tone={workflowId ? "violet" : "brand"}>
                {workflowId ? "Multi-agent" : "Agent Filter"}
              </Badge>
            )}
          </div>
          
        </div>
        <div className="flex items-center gap-3">
          <FilterPopover value={filters} onChange={updateFilters} />
          <DateRangePicker value={range} onChange={setRange} align="right" />
        </div>
      </div>

      {/* Where the selected campaign's data actually lives (start/end dates).
          Shown whenever a campaign is selected, and whenever the current
          range is empty so "Last 7 Days + campaign" never looks broken. */}
      {!loading && (filters.campaign || data.kpis.totalCalls === 0) && data.dataAvailability && (
        <DataAvailabilityBanner
          availability={data.dataAvailability}
          campaign={filters.campaign}
          totalCalls={data.kpis.totalCalls}
          onJumpToData={(start, end) => setRange({ start, end, preset: null })}
        />
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard 
          loading={loading} 
          label="Total Calls" 
          value={data.kpis.totalCalls.toLocaleString("en-IN")} 
          subtitle={`${data.kpis.uniqueContacts} unique attempts\n${data.kpis.retryCalls} retries`}
          tooltip="Total number of calls made"
          modalTitle="Total Calls Breakdown"
          modalContent={
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Total</span>
                <span className="font-semibold text-ink">{data.kpis.totalCalls}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Unique Contacts</span>
                <span className="font-semibold text-ink">{data.kpis.uniqueContacts}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Retries</span>
                <span className="font-semibold text-ink">{data.kpis.retryCalls}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Completed</span>
                <span className="font-semibold text-ink">{data.kpis.connectedCalls}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-ink-2">Failed</span>
                <span className="font-semibold text-ink">{data.kpis.callsFailed}</span>
              </div>
            </div>
          }
        />

        <StatCard 
          loading={loading} 
          label="Calls Connected" 
          value={data.kpis.uniqueReached.toLocaleString("en-IN")} 
          subtitle={`out of ${data.kpis.uniqueContacts} unique\n${data.kpis.uniqueNotReached} not reached`}
          tooltip="Unique contacts who answered at least once"
          modalTitle="Connected Calls Breakdown"
          modalContent={
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Unique Reached</span>
                <span className="font-semibold text-ink">{data.kpis.uniqueReached}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Unique Not Reached</span>
                <span className="font-semibold text-ink">{data.kpis.uniqueNotReached}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Total Unique Contacts</span>
                <span className="font-semibold text-ink">{data.kpis.uniqueContacts}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-ink-2">Raw Completed Attempts</span>
                <span className="font-semibold text-ink">{data.kpis.connectedCalls}</span>
              </div>
            </div>
          }
        />

        <StatCard 
          loading={loading} 
          label="Connectivity" 
          value={`${data.kpis.uniqueConnectivityPercentage}%`} 
          subtitle={`${data.kpis.uniqueReached} / ${data.kpis.uniqueContacts} unique reached`}
          tooltip="Percentage of unique contacts reached"
          modalTitle="Connectivity Analysis"
          modalContent={
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Unique Connectivity</span>
                <span className="font-semibold text-ink">{data.kpis.uniqueConnectivityPercentage}%</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Per-Attempt Connectivity</span>
                <span className="font-semibold text-ink">{data.kpis.connectivityPercentage}%</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Unique Contacts</span>
                <span className="font-semibold text-ink">{data.kpis.uniqueContacts}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Unique Reached</span>
                <span className="font-semibold text-[#16a34a]">{data.kpis.uniqueReached}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Not Reached</span>
                <span className="font-semibold text-[#ef4444]">{data.kpis.uniqueNotReached}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-ink-2">Retry Attempts</span>
                <span className="font-semibold text-ink">{data.kpis.retryCalls}</span>
              </div>
            </div>
          }
        />

        <StatCard 
          loading={loading} 
          label="Avg. Duration" 
          value={formatDuration(data.kpis.avgDurationSec)} 
          tooltip="Average call duration"
          modalTitle="Duration Distribution"
          modalContent={
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">0-10s</span>
                <span className="font-semibold text-ink">{data.kpis.durationBreakdown?.['0-10s'] || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">10-30s</span>
                <span className="font-semibold text-ink">{data.kpis.durationBreakdown?.['10-30s'] || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">30-60s</span>
                <span className="font-semibold text-ink">{data.kpis.durationBreakdown?.['30-60s'] || 0}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-ink-2">&gt;60s</span>
                <span className="font-semibold text-ink">{data.kpis.durationBreakdown?.['>60s'] || 0}</span>
              </div>
            </div>
          }
        />

        <StatCard
          loading={loading}
          label="Credits Used"
          value={formatCredits(data.kpis.creditsUsed)}
          subtitle={`${data.kpis.connectedCalls} billable calls\n${data.kpis.creditsPulseSeconds}s pulse`}
          tooltip="Credits consumed by the calls matching the current filters. Only connected calls are billed, and each is rounded up to a whole pulse."
          modalTitle="Credits Breakdown"
          modalContent={
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Total Credits</span>
                <span className="font-semibold text-ink">{formatCredits(data.kpis.creditsUsed)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Billable Calls</span>
                <span className="font-semibold text-ink">{data.kpis.connectedCalls}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Pulse Rate</span>
                <span className="font-semibold text-ink">{data.kpis.creditsPulseSeconds}s</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2">Billed Per Call</span>
                <span className="font-semibold text-ink">{data.kpis.creditsBilledCalls}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-ink-2">Estimated From Duration</span>
                <span className="font-semibold text-ink">{data.kpis.creditsRecomputedCalls}</span>
              </div>
            </div>
          }
        />
      </div>

      {/* Row: bars + donut */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <ChartCard loading={loading} title="Calls per Day" span2>
          <CallsPerDayChart data={data.callsPerDay} />
        </ChartCard>
        <ChartCard loading={loading} title="What Happened to Calls">
          <OutcomeDonut data={data.outcomes} />
        </ChartCard>
      </div>

      {/* Pickup timing */}
      <div>
        <ChartCard
          loading={loading}
          title="When People Pick Up"
          aside={(
            <Badge tone={bestPickupWindow ? "brand" : "neutral"}>
              {bestPickupWindow ? `Best window: ${bestPickupWindow}` : "No pickup data"}
            </Badge>
          )}
        >
          <HourlyPickupChart data={data.hourlyPickup} />
        </ChartCard>
      </div>

      {/* Export */}
      <div className="rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-ink">Export Report</h2>
            <p className="text-sm text-ink-2">Build a shareable report from the charts above.</p>
          </div>
          <button
            onClick={() => setExportOpen(true)}
            className="rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover"
          >
            Generate Export
          </button>
        </div>

        <div className="mt-5 border-t border-line pt-4">
          <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Recent Exports</p>
          <ul className="space-y-2">
            {recentExports.map((ex) => (
              <li key={ex.id} className="flex items-center gap-3 rounded-lg bg-soft px-4 py-2.5">
                <span className="flex-1 truncate font-mono text-sm text-ink">{ex.name}</span>
                <span className="text-xs text-ink-3">{ex.createdAt}</span>
                <Badge tone="success">Ready</Badge>
                <button aria-label={`Download ${ex.name}`}
                  onClick={() => exportAnalytics(ex.format, false).catch((error) => toast.danger("Download failed", (error as Error).message))}
                  className="rounded-lg p-1.5 text-brand hover:bg-brand-soft">
                  <Download className="h-4 w-4" />
                </button>
              </li>
            ))}
            {recentExports.length === 0 && (
              <li className="rounded-lg bg-soft px-4 py-3 text-sm text-ink-3">
                No exports generated in this session.
              </li>
            )}
          </ul>
        </div>
      </div>

      {exportOpen && (
        <ExportModal
          range={range}
          onExport={(format) => exportAnalytics(format)}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
}

/* ── Local presentational helpers ── */

function StatCard({ label, value, delta, deltaUnit = "%", invert = false, loading = false, subtitle, tooltip, modalTitle, modalContent }: {
  label: string; value: string; delta?: number; deltaUnit?: "%" | "pp"; invert?: boolean; loading?: boolean; subtitle?: string; tooltip?: string; modalTitle?: string; modalContent?: React.ReactNode;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const showDelta = delta !== undefined;
  const unchanged = showDelta && Math.abs(delta) < 0.05;
  const good = showDelta && (invert ? delta < 0 : delta > 0);
  const tone = unchanged ? "neutral" : good ? "success" : "danger";
  const direction = unchanged ? "" : (showDelta && delta > 0) ? "▲ " : "▼ ";
  const absoluteDelta = showDelta ? Math.abs(delta) : 0;
  const formattedDelta = Number.isInteger(absoluteDelta)
    ? absoluteDelta.toFixed(0)
    : absoluteDelta.toFixed(1);

  const handleClick = () => {
    if (modalContent) setModalOpen(true);
  };

  return (
    <>
      <div 
        className={cn("relative rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-4 transition-opacity group", loading && "opacity-50", modalContent && "cursor-pointer")} 
        onClick={handleClick}
      >
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="absolute top-4 right-4 text-ink-3 hover:text-ink transition-colors" onClick={(e) => e.stopPropagation()}>
                  <Info className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">{label}</p>
        <p className="mt-2 font-mono text-2xl font-bold text-ink">{value}</p>
        {subtitle && (
          <p className="mt-1 text-xs text-ink-3 whitespace-pre-line">{subtitle}</p>
        )}
        {showDelta && (
          <Badge tone={tone} className="mt-2">
            {direction}{formattedDelta}{deltaUnit === "pp" ? " pp" : "%"} vs last period
          </Badge>
        )}
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-brand transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
      </div>

      {modalContent && (
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-lg bg-elevated">
            <DialogHeader>
              <DialogTitle className="text-sm font-semibold text-ink">{modalTitle || label}</DialogTitle>
              <DialogDescription className="text-ink-2">Detailed breakdown</DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              {modalContent}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function ChartCard({ title, aside, span2 = false, loading = false, children }: {
  title: string; aside?: React.ReactNode; span2?: boolean; loading?: boolean; children: React.ReactNode;
}) {
  return (
    <div className={cn("rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-4 sm:p-5 transition-opacity", span2 && "xl:col-span-2", loading && "opacity-50")}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        {aside}
      </div>
      {children}
    </div>
  );
}
